import React, { useState } from 'react'
import { useLogin, useNotify } from 'react-admin'
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core/styles'
import { Auth } from 'aws-amplify'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import styled from 'styled-components'
import { useBrand } from '../hooks/useBrand'

// Brand colors – marketing-aligned but light main area
const BRAND = {
  orange: '#FF4B1B',
  orangeDark: '#D32C16',
  headerBg: '#161921',
  bgPage: '#f8f9fb',
  bgCard: '#ffffff',
  text: '#1a1d24',
  textMuted: 'rgba(26, 29, 36, 0.65)',
  border: 'rgba(0, 0, 0, 0.12)',
  borderHover: 'rgba(0, 0, 0, 0.24)',
}

// Subtle plus-grid like marketing hero (very light on white)
const PLUS_GRID = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cpath fill='rgba(26,29,36,0.035)' d='M14 2h4v28h-4zM2 14h28v4H2z'/%3E%3C/svg%3E")`

const loginTheme = createMuiTheme({
  palette: {
    type: 'light',
    primary: { main: BRAND.orange },
    background: { default: BRAND.bgPage, paper: BRAND.bgCard },
    text: { primary: BRAND.text, secondary: BRAND.textMuted },
  },
  overrides: {
    MuiOutlinedInput: {
      root: {
        backgroundColor: '#ffffff',
        borderRadius: 10,
        '& $notchedOutline': { borderColor: BRAND.border },
        '&:hover $notchedOutline': { borderColor: BRAND.borderHover },
        '&$focused $notchedOutline': { borderColor: BRAND.orange, borderWidth: 2 },
        '& .MuiOutlinedInput-input': { color: BRAND.text, fontSize: 16 },
      },
    },
    MuiInputLabel: {
      outlined: {
        color: BRAND.textMuted,
        '&$focused': { color: BRAND.orange },
      },
    },
    MuiInputBase: {
      input: { '&::placeholder': { color: BRAND.textMuted, opacity: 0.8 } },
    },
    MuiButton: {
      containedPrimary: {
        backgroundColor: BRAND.orange,
        color: '#ffffff',
        '&:hover': { backgroundColor: BRAND.orangeDark },
        borderRadius: 10,
        padding: '12px 24px',
        textTransform: 'none',
        fontSize: '1rem',
        fontWeight: 500,
        minHeight: 48,
      },
    },
    MuiIconButton: {
      root: {
        color: BRAND.textMuted,
        '&:hover': { color: BRAND.orange, backgroundColor: 'rgba(255, 75, 27, 0.06)' },
        padding: 8,
      },
    },
  },
})

const useStyles = makeStyles(theme => ({
  input: { marginBottom: 24 },
  submit: { marginTop: 12, marginBottom: 20 },
  forgot: {
    color: BRAND.textMuted,
    fontSize: '0.9375rem',
    textDecoration: 'none',
    '&:hover': { color: BRAND.orange, textDecoration: 'none' },
  },
}))

const FORGOT_PWD = { OFF: 0, SEND_CODE: 1, RESET_PWD: 2 }

const PasswordField = ({ label, value, onChange, showPassword, onToggleVisibility, disabled, style }) => (
  <TextField
    fullWidth
    label={label}
    name="password"
    type={showPassword ? 'text' : 'password'}
    value={value}
    onChange={onChange}
    variant="outlined"
    disabled={disabled}
    style={style}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton aria-label="toggle password visibility" onClick={onToggleVisibility} edge="end">
            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
)

const LoginPage = ({ theme: _raTheme }) => {
  const brand = useBrand()
  const classes = useStyles()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConf, setNewPasswordConf] = useState('')
  const [newPwdMode, setNewPwdMode] = useState(false)
  const [forgotPwdMode, setForgotPwdMode] = useState(FORGOT_PWD.OFF)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showNewPasswordConf, setShowNewPasswordConf] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const login = useLogin()
  const notify = useNotify()

  const submit = async e => {
    e.preventDefault()
    setError('')
    if (forgotPwdMode === FORGOT_PWD.RESET_PWD && newPassword !== newPasswordConf) {
      setError('Passwords do not match.')
      return
    }
    setIsSubmitting(true)
    try {
      if (forgotPwdMode === FORGOT_PWD.SEND_CODE) {
        await Auth.forgotPassword(email)
        setForgotPwdMode(FORGOT_PWD.RESET_PWD)
        return
      }
      if (forgotPwdMode === FORGOT_PWD.RESET_PWD) {
        await Auth.forgotPasswordSubmit(email, code, newPassword)
        setForgotPwdMode(FORGOT_PWD.OFF)
        setPassword('')
        setCode('')
        return
      }
      await login({ email, password, newPassword })
    } catch (err) {
      if (err.message === 'NEW_PASSWORD_REQUIRED') return setNewPwdMode(true)
      if (err.code === 'InvalidPasswordException') {
        setError(err.message)
        return
      }
      if (err.code === 'NotAuthorizedException' || err.code === 'UserNotFoundException') {
        setError('Invalid email or password.')
        return
      }
      if (err.code === 'UserNotConfirmedException') {
        setError('Your account is not yet confirmed. Please check your email.')
        return
      }
      if (err.code === 'PasswordResetRequiredException') {
        setError('Your password must be reset. Please use Forgot password below.')
        return
      }
      if (err.message === 'Unauthorized' || err.message === 'Request failed with status code 401') {
        setError('Your session could not be verified. Please try again or contact support.')
        return
      }
      const msg = err?.message || ''
      if (msg === 'Failed to fetch' || msg === 'Load failed' || msg === 'NetworkError' || (err?.name === 'TypeError' && msg.includes('fetch'))) {
        setError('Unable to connect. Please check your network and try again.')
        return
      }
      setError(err?.message && err.message.length < 120 ? err.message : 'Sign-in failed. Please try again or contact support.')
      notify(err?.message && err.message.length < 120 ? err.message : 'Sign-in failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ThemeProvider theme={loginTheme}>
      <Wrapper>
        <HeaderBar>
          <LogoLink href="/">
            <Logo src={brand.logo} alt="EfficienseeAI" />
          </LogoLink>
        </HeaderBar>
        <Main>
          <FormCard>
            <FormHeading>Sign in</FormHeading>
            <Form onSubmit={submit}>
              {!newPwdMode && forgotPwdMode !== FORGOT_PWD.RESET_PWD ? (
                <>
                  {forgotPwdMode === FORGOT_PWD.SEND_CODE && (
                    <FormHint>Enter your email to receive a password reset code.</FormHint>
                  )}
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    variant="outlined"
                    disabled={isSubmitting}
                    className={classes.input}
                  />
                  {!forgotPwdMode && (
                    <PasswordField
                      label="Password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      showPassword={showPassword}
                      onToggleVisibility={() => setShowPassword(!showPassword)}
                      disabled={isSubmitting}
                      style={{ marginBottom: 24 }}
                    />
                  )}
                </>
              ) : (
                <>
                  {forgotPwdMode === FORGOT_PWD.RESET_PWD && (
                    <TextField
                      fullWidth
                      label="Verification code"
                      name="code"
                      value={code}
                      onChange={e => setCode(e.target.value)}
                      variant="outlined"
                      disabled={isSubmitting}
                      className={classes.input}
                    />
                  )}
                  <PasswordField
                    label="New password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    showPassword={showNewPassword}
                    onToggleVisibility={() => setShowNewPassword(!showNewPassword)}
                    disabled={isSubmitting}
                    style={{ marginBottom: 24 }}
                  />
                  <PasswordField
                    label="Confirm new password"
                    value={newPasswordConf}
                    onChange={e => setNewPasswordConf(e.target.value)}
                    showPassword={showNewPasswordConf}
                    onToggleVisibility={() => setShowNewPasswordConf(!showNewPasswordConf)}
                    disabled={isSubmitting}
                    style={{ marginBottom: 24 }}
                  />
                </>
              )}
              {error && <Error>{error}</Error>}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting}
                className={classes.submit}
                startIcon={isSubmitting ? <CircularProgress size={18} style={{ color: 'white' }} /> : null}
              >
                {isSubmitting
                  ? newPwdMode
                    ? 'Updating…'
                    : forgotPwdMode
                    ? forgotPwdMode === FORGOT_PWD.SEND_CODE
                      ? 'Sending code…'
                      : 'Resetting…'
                    : 'Signing in…'
                  : newPwdMode
                  ? 'Update password'
                  : forgotPwdMode
                  ? forgotPwdMode === FORGOT_PWD.SEND_CODE
                    ? 'Send code'
                    : 'Reset password'
                  : 'Sign in'}
              </Button>
              <ForgotButton
                type="button"
                className={classes.forgot}
                disabled={isSubmitting}
                onClick={() => {
                  setForgotPwdMode(prev =>
                    prev === FORGOT_PWD.SEND_CODE || prev === FORGOT_PWD.RESET_PWD ? FORGOT_PWD.OFF : FORGOT_PWD.SEND_CODE
                  )
                  setError('')
                }}
              >
                {!forgotPwdMode ? 'Forgot password?' : 'Back to sign in'}
              </ForgotButton>
            </Form>
          </FormCard>
        </Main>
      </Wrapper>
    </ThemeProvider>
  )
}

const Wrapper = styled.div`
  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const HeaderBar = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  min-height: 64px;
  padding-top: env(safe-area-inset-top, 0px);
  padding-right: max(24px, env(safe-area-inset-right));
  padding-bottom: 0;
  padding-left: max(24px, env(safe-area-inset-left));
  background: ${BRAND.headerBg};

  @media (max-width: 480px) {
    min-height: 56px;
    padding-left: max(20px, env(safe-area-inset-left));
    padding-right: max(20px, env(safe-area-inset-right));
  }
`

const LogoLink = styled.a`
  text-decoration: none;
`

const Logo = styled.img`
  height: 28px;
  width: auto;
  display: block;
  max-width: 160px;
  object-fit: contain;

  @media (max-width: 380px) {
    height: 24px;
    max-width: 130px;
  }
`

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background-color: ${BRAND.bgPage};
  background-image: ${PLUS_GRID},
    radial-gradient(ellipse 55% 35% at 100% 0%, rgba(255, 75, 27, 0.06) 0%, transparent 55%);
  background-size: 32px 32px, 100% 100%;
  padding: 48px 32px 56px;
  padding-right: max(32px, env(safe-area-inset-right));
  padding-bottom: max(56px, env(safe-area-inset-bottom));
  padding-left: max(32px, env(safe-area-inset-left));

  @media (max-width: 480px) {
    justify-content: flex-start;
    padding: 32px 20px 40px;
    padding-top: 32px;
    padding-right: max(20px, env(safe-area-inset-right));
    padding-bottom: max(40px, env(safe-area-inset-bottom));
    padding-left: max(20px, env(safe-area-inset-left));
  }
`

const FormCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 400px;
  min-width: 0;
  background: ${BRAND.bgCard};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  padding: 48px 44px 52px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);

  @media (max-width: 480px) {
    padding: 32px 24px 36px;
    border-radius: 14px;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
  }
`

const FormHeading = styled.h1`
  margin: 0 0 32px 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${BRAND.text};
  letter-spacing: -0.02em;
  line-height: 1.3;

  @media (max-width: 480px) {
    margin-bottom: 28px;
    font-size: 1.375rem;
  }
`

const FormHint = styled.p`
  margin: 0 0 20px 0;
  font-size: 0.9375rem;
  color: ${BRAND.textMuted};
  line-height: 1.5;

  @media (max-width: 480px) {
    margin-bottom: 16px;
    font-size: 0.9rem;
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
`

const Error = styled.span`
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 14px;
  line-height: 1.4;
`

const ForgotButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 14px 0;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font: inherit;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    min-height: 44px;
    padding: 12px 0;
  }
`

export default LoginPage
